/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageErrorWrapper } from "@insite/client-framework/Components/PageErrorWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import loadQuoteIfNeeded from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/LoadQuoteIfNeeded";
import setExpirationDate from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/SetExpirationDate";
import { MetaDescription, MetaKeywords } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, LoadingSpinner } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ quoteId?: string }>(search);
    const quoteId = parsedQuery.quoteId;

    return {
        quoteId,
        quoteState: getQuoteState(state, quoteId),
    };
};

const mapDispatchToProps = {
    loadQuoteIfNeeded,
    setExpirationDate,
};

export interface RfqQuoteDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    // XNG-Changes
    spinnerWrapper?: InjectableCss;
    headerGridItem?: GridItemProps;
    infoGridItem?: GridItemProps;
    quoteLinesGridItem?: GridItemProps;
    contentOneGridItem?: GridItemProps;
    contentTwoGridItem?: GridItemProps;
    repInfoGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    linesGridItem?: GridItemProps;
}

export const rfqQuoteDetailsPageStyles: RfqQuoteDetailsPageStyles = {
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: {
        weight: "bold",
    },
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RfqQuoteDetailsPage: FC<Props> = ({ id, quoteId, quoteState, loadQuoteIfNeeded, setExpirationDate }) => {
    useEffect(() => {
        if (quoteId && !quoteState.isLoading && !quoteState.errorStatusCode) {
            loadQuoteIfNeeded({ quoteId });
        }
    }, [quoteId, quoteState.isLoading, quoteState.value]);

    useEffect(() => {
        setExpirationDate({ expirationDate: undefined });
    }, []);

    // XNG-Change
    const styles = rfqQuoteDetailsPageStyles;

    if (quoteState && quoteState.errorStatusCode === 404) {
        return (
            <Page>
                <PageErrorWrapper
                    loadFailedTextTestSelector="rfqQuoteDetails_orderNotFound"
                    value={quoteState}
                    extendedStyles={styles}
                    notFoundMessage={siteMessage("Rfq_OrderNotFound")}
                ></PageErrorWrapper>
            </Page>
        );
    }

    // XNG-Change
    if (quoteState && !quoteState.value) {
        return (
            <Page>
                <StyledWrapper {...styles.spinnerWrapper}>
                    <LoadingSpinner color="primary" />
                </StyledWrapper>
            </Page>
        );
    }

    // XNG-Change: add layout
    return (
        <Page>
            <GridContainer>
                <GridItem {...styles.headerGridItem}>
                    <Zone contentId={id} zoneName="HeaderWidget" />
                </GridItem>
                <GridItem {...styles.contentOneGridItem}>
                    <GridContainer>
                        <GridItem {...styles.infoGridItem}>
                            <Zone contentId={id} zoneName="InfoWidget" />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.contentTwoGridItem}>
                    <GridContainer>
                        <GridItem {...styles.repInfoGridItem}>
                            <Zone contentId={id} zoneName="RepInfoWidget" />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.notesGridItem}>
                    <Zone contentId={id} zoneName="NotesWidget" />
                </GridItem>
                <GridItem {...styles.linesGridItem}>
                    <GridContainer>
                        <GridItem {...styles.quoteLinesGridItem}>
                            <Zone contentId={id} zoneName="QuoteLinesWidget" />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        // XNG-Change
        fieldDefinitions: [MetaKeywords, MetaDescription],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "RfqQuoteDetailsPage" instead of this constant.
 */
export const RfqQuoteDetailsPageContext = "RfqQuoteDetailsPage";
